import * as brazeSDK from '@braze/web-sdk'
import * as Sentry from '@sentry/nuxt'
import { useToast } from 'vue-toast-notification'
import { checkIntegrationSwitch } from '~/helpers/config'
import { useContentActions, IAM_CONTENT_ACTIONS, SUCCESS_GATED_ACTIONS } from '~/composables/useContentActions'
import type { CampaignRuleVerdict } from '~/composables/useBrazeCampaignRules'

const NO_APPLICABLE_PROMOTION = 'no_applicable_promotion'

interface BrazePluginApi {
  initBrazeSDK: () => boolean // Braze returns false for cases like crawler bots and opted-out users
  debugLogger: (msg: string) => void
  sdk: typeof brazeSDK
}

const createDisabledHandler = () => {
  return {
    get: (target: any, prop: string) => {
      return (...args: any[]) => {
        if (import.meta.dev) {
          console.warn(
            `Attempted to call Braze SDK method "${prop}" but Braze integration is disabled.\n`,
            `Arguments:`,
            args,
          )
        }
        return undefined
      }
    },
  }
}

/**
 * Braze SDK Client-side Nuxt Plugin
 * Provides a wrapper around Braze Web SDK with feature flag control.
 *
 * @example
 * // Enable plugin via nuxt.confg.ts public runtime config
 * frontendIntegrationSwitches: 'braze'
 *
 * // Usage in components
 * $braze.initBrazeSDK()
 * useBrazeTracking().logCustomEvent('eventName')
 * $braze.debugLogger('Debug message')
 *
 * @see {@link https://js.appboycdn.com/web-sdk/latest/doc/modules/braze.html Braze Web SDK Documentation}
 *
 * @returns Plugin providing Braze SDK instance
 * @remarks
 * - Returns proxied no-op implementation when feature flag is disabled
 * - Lazily imports Braze SDK only when enabled
 * - Requires manual initialization via initBrazeSDK()
 */
export default defineNuxtPlugin({
  name: 'braze',
  parallel: true,

  setup() {
    const { public: publicConfig } = useRuntimeConfig()

    // Early return with empty implementation (via Proxy handler) if disabled
    if (!checkIntegrationSwitch(publicConfig.frontendIntegrationSwitches, 'braze')) {
      // SAFETY: the Proxy answers every SDK member with a no-op, so this satisfies the `sdk`
      // contract at runtime without structurally matching the SDK module type.
      return {
        provide: {
          braze: {
            initBrazeSDK: () => false,
            debugLogger: () => {},
            // SAFETY: the Proxy answers every property read with a no-op function, so it stands in
            // for the whole SDK surface while the integration switch is off.
            sdk: new Proxy({}, createDisabledHandler()) as typeof brazeSDK,
          } satisfies BrazePluginApi,
        },
      }
    }

    // SAFETY: `sdk` is a getter rather than a property, which the plugin API's structural check
    // can't see through; every member of BrazePluginApi is implemented below.
    return {
      provide: {
        braze: {
          // We must give app context the control of when and where to init the Braze SDK
          initBrazeSDK: () => {
            const initSuccess = brazeSDK.initialize(publicConfig.brazeSdkApiKey, {
              baseUrl: publicConfig.brazeSdkBaseUrl,
              enableLogging: import.meta.dev,
              doNotLoadFontAwesome: false, // web-sdk package provides OOTB UI: disable this typeface for production but enabling for content card discovery
              allowUserSuppliedJavascript: true, // To enable in app messaging this needs to be set to true
            })

            if (!initSuccess && import.meta.dev) {
              console.warn('Braze SDK did not initialize - see Braze logs for the reason.')
            }

            return initSuccess
          },

          // Debug logging utility that is ONLY run in dev mdoe, which is separate to Braze SDK's log messages
          debugLogger: (msg: string) => {
            if (import.meta.dev && msg) {
              console.debug('Braze SDK Plugin: ', msg)
            }
          },

          // Expose the Braze SDK library methods directly for use
          get sdk() {
            if (!brazeSDK) {
              throw new Error('Braze SDK not initialized. Call initBrazeSDK first')
            }
            return brazeSDK
          },
        } satisfies BrazePluginApi,
      },
    }
  },

  hooks: {
    'app:created'() {
      // Initialize Braze SDK for every session / page
      // TODO [2024-12-17, JS]: REVIEW This is the first phase assumption - depending on cost impact we may want to conditionalise when to initialize
      const { $braze } = useNuxtApp()
      const { t } = useNuxtApp().$i18n
      const toast = useToast()

      if (!$braze.initBrazeSDK()) {
        return
      }

      const couponApplyInFlight = new Set<string>()
      // Built in Nuxt context: the SDK callback below runs outside it.
      const campaignRules = useBrazeCampaignRules()

      const handleInAppMessage = (inAppMessage: brazeSDK.InAppMessage | brazeSDK.ControlMessage) => {
        // ControlMessage is delivered to users in a campaign's control group — it has no
        // visible UI but must be "shown" to correctly log the impression in Braze.
        // Standard boilerplate for any subscribeToInAppMessage implementation.
        if (inAppMessage instanceof brazeSDK.ControlMessage) {
          return $braze.sdk.showInAppMessage(inAppMessage)
        }

        // Coupon code is passed via Braze extras KVP (key: coupon_code) rather than reading
        // from preferencesStore.signupCouponCode.
        const couponCode = inAppMessage.extras?.['coupon_code']

        // Action KVP — CRM sets extras key `action` to any allowed value from CONTENT_ACTIONS
        // (e.g. `mhj-signup-panel`) to trigger a frontend action from a Modal/FullScreen IAM.
        // Matched against the IAM allow-list here, so anything else is ignored as unauthored.
        const action = IAM_CONTENT_ACTIONS.find((allowed) => allowed === inAppMessage.extras?.['action'])

        const campaign = readBrazeCampaign((key) => inAppMessage.extras?.[key], 'iam')

        let closeBridge: (() => void) | undefined

        // The bridge belongs to one message, so a stacked message must not clear another's.
        const releaseCloseBridge = () => {
          if (closeBridge && window.__brazeIamClose === closeBridge) delete window.__brazeIamClose
        }

        // Braze's dismissed event carries no reason, so the close is observed from the DOM and
        // reported as `dismiss_type` (#261189). See utils/brazeIamDismissal.ts.
        const dismissal = watchIamDismissalIntent(
          inAppMessage.dismissType === brazeSDK.InAppMessage.DismissType.AUTO_DISMISS
            ? (inAppMessage.duration ?? 0)
            : undefined,
          releaseCloseBridge,
        )

        inAppMessage.subscribeToDismissedEvent(() => {
          releaseCloseBridge()
          const dismissType = dismissal.settle()
          if (!dismissType) return

          useBrazeTracking().logCustomEvent('iam_dismiss', {
            page_url: window.location.href,
            dismiss_type: dismissType,
            ...campaign,
          })
          $braze.debugLogger(`IAM dismiss logged: ${dismissType}`)
        })

        // Braze refuses to display in cases such as a modal already being on screen, and starts the
        // message's close timer from its display callback.
        const showMessage = () => {
          const shown = $braze.sdk.showInAppMessage(inAppMessage, undefined, dismissal.markDisplayed)
          if (!shown) dismissal.dispose()
          else dismissal.bindDisplayedRoot()
          return shown
        }

        // Snapshot page_url/content_type before a gated dispatch can navigate away from this page.
        const captureIamClickContext = () => ({
          pageUrl: window.location.href,
          contentType: usePageContentType().contentType.value,
        })

        // Shared iam_click logger — content_type defaults to the live page unless a snapshot is passed in.
        const logIamClick = (
          ctaText: string | undefined,
          context: ReturnType<typeof captureIamClickContext> = captureIamClickContext(),
        ) => {
          const text = sanitizeCampaignText(ctaText) || 'iam - no ctaText'
          useBrazeTracking().logCustomEvent(
            'iam_click',
            {
              page_url: context.pageUrl,
              cta_text: text,
              ...campaign,
            },
            { contentType: context.contentType },
          )
          $braze.debugLogger(`IAM click logged: ${text}`)
        }

        // Gated actions (navigate, copyCode) log their click once dispatch succeeds, never from the
        // per-button/bridge click logger below.
        const gated = !!action && SUCCESS_GATED_ACTIONS.includes(action)

        // The one shared definition of "which extras key names the gated CTA's label" — both
        // suppression (__brazeIamClick) and resolution (below) read this list, so they can't drift.
        const PRIMARY_CTA_KEYS = ['ctaText', 'cta1Text', 'cta1_text', 'cta_text'] as const
        const resolvePrimaryCtaText = () => PRIMARY_CTA_KEYS.map((key) => inAppMessage.extras?.[key]).find(Boolean)

        // Snapshot → dispatch → conditional log, shared by the native button and Custom HTML paths.
        // Runs for any IAM action; only SUCCESS_GATED_ACTIONS values log here (derived from the param,
        // not the outer `action`/`gated`, so this stays correct if ever called for a different action).
        const dispatchIamAction = async (iamAction: ContentAction, ctaText: string | undefined, surface: string) => {
          const clickContext = captureIamClickContext()
          const succeeded = await useContentActions().dispatch(
            iamAction,
            { url: inAppMessage.extras?.['url'], couponCode: inAppMessage.extras?.['couponCode'] },
            campaign,
          )
          if (SUCCESS_GATED_ACTIONS.includes(iamAction) && succeeded) logIamClick(ctaText, clickContext)
          $braze.debugLogger(`IAM ${surface} action dispatched: ${iamAction}`)
        }

        // Fires iam_click per native button. Button 0's label always resolves via PRIMARY_CTA_KEYS (not
        // just cta1Text/cta1_text) so it matches the gated path below regardless of gated state.
        // Subscribed before the action handler so a click still logs even when that handler closes it.
        if (inAppMessage instanceof brazeSDK.ModalMessage || inAppMessage instanceof brazeSDK.FullScreenMessage) {
          inAppMessage.buttons?.forEach((button, index) => {
            if (gated && index === 0) return
            button.subscribeToClickedEvent(() => {
              logIamClick(
                index === 0
                  ? (resolvePrimaryCtaText() ?? button.text)
                  : (inAppMessage.extras?.[`cta${index + 1}Text`] ??
                      inAppMessage.extras?.[`cta${index + 1}_text`] ??
                      button.text),
              )
            })
          })
        }

        if (
          action &&
          (inAppMessage instanceof brazeSDK.ModalMessage || inAppMessage instanceof brazeSDK.FullScreenMessage) &&
          inAppMessage.buttons?.length
        ) {
          const actionButton = inAppMessage.buttons[0]
          actionButton.subscribeToClickedEvent(async () => {
            try {
              await dispatchIamAction(action, resolvePrimaryCtaText() ?? actionButton.text, 'native')
            } finally {
              dismissal.suppress()
              inAppMessage.closeMessage()
            }
          })
        }

        // Custom HTML path — coupon code and content actions exposed as window globals for the
        // iframe to call.
        if (inAppMessage instanceof brazeSDK.HtmlMessage) {
          if (couponCode) {
            window.__brazeCouponCode = couponCode
            window.__brazeApplyCoupon = async () => {
              delete window.__brazeApplyCoupon
              delete window.__brazeCouponCode
              try {
                const existingCoupon = useBasketsStore().basket?.couponItems?.find((item) => item.code === couponCode)
                const alreadyApplied =
                  existingCoupon?.valid === true && existingCoupon?.statusCode !== NO_APPLICABLE_PROMOTION
                // Another handler is already applying this code — let its outcome stand.
                if (!existingCoupon && couponApplyInFlight.has(couponCode)) return
                const { applyCoupon } = useBasket()
                let success: boolean | undefined
                if (existingCoupon) {
                  success = false
                } else {
                  couponApplyInFlight.add(couponCode)
                  try {
                    success = await applyCoupon(couponCode)
                  } finally {
                    couponApplyInFlight.delete(couponCode)
                  }
                  const appliedItem = useBasketsStore().basket?.couponItems?.find((item) => item.code === couponCode)
                  if (success && appliedItem?.statusCode === NO_APPLICABLE_PROMOTION) {
                    success = false
                  }
                }
                if (success === undefined) {
                  // No basket exists — user hasn't added items yet
                  toast.default(t('checkout.order_summary.iam_coupon_no_basket', { code: couponCode }), {
                    duration: 5000,
                    position: 'top',
                  })
                } else if (alreadyApplied) {
                  toast.error(t('checkout.order_summary.iam_coupon_already_applied'), {
                    duration: 5000,
                    position: 'top',
                  })
                } else if (!success) {
                  // SFCC rejected the coupon, or it's on the basket but ineligible.
                  toast.error(t('checkout.order_summary.iam_coupon_apply_error', { code: couponCode }), {
                    duration: 5000,
                    position: 'top',
                  })
                }
                $braze.debugLogger(`IAM HTML coupon application: ${couponCode} — ${success ? 'success' : 'failed'}`)
              } finally {
                // As with the action bridge: closing here keeps the template from raising a dismissal
                // that would be reported beside the CTA's own iam_click.
                dismissal.suppress()
                inAppMessage.closeMessage()
              }
            }
          }

          // Action KVP for Custom HTML IAMs (e.g. slide-up). The HTML calls
          // window.parent.__brazeDispatchAction() from a button onclick.
          delete window.__brazeDispatchAction
          if (action) {
            window.__brazeDispatchAction = async () => {
              delete window.__brazeDispatchAction
              try {
                await dispatchIamAction(action, resolvePrimaryCtaText(), 'HTML')
              } finally {
                // The bridge closes the message so the template doesn't have to: `navigate` is a
                // router change that leaves the IAM on screen, and a template-side close would
                // raise an unsuppressed dismissal alongside the CTA's own iam_click.
                dismissal.suppress()
                inAppMessage.closeMessage()
              }
            }
          }

          // HTML IAMs run in an iframe so the SDK can't see their buttons — the CTA passes its label's
          // extras key (e.g. 'cta1Text'). A gated action's own CTA (any PRIMARY_CTA_KEYS key, or none)
          // is suppressed here; any other key still logs immediately.
          delete window.__brazeIamClick
          window.__brazeIamClick = (ctaKey?: string) => {
            if (gated && (ctaKey === undefined || PRIMARY_CTA_KEYS.some((key) => key === ctaKey))) return
            logIamClick(inAppMessage.extras?.[ctaKey ?? 'ctaText'] || resolvePrimaryCtaText())
          }

          // An HTML IAM closes from inside its iframe, so the template marks it first:
          //   onclick="window.parent.__brazeIamClose?.(); brazeBridge.closeMessage()"
          const shown = showMessage()
          if (shown) {
            closeBridge = () => {
              dismissal.markExplicitClose()
            }
            window.__brazeIamClose = closeBridge
          }
          return shown
        }

        // ModalMessage / FullScreenMessage path — wire the primary CTA button to apply the
        // coupon via the same applyCoupon() from useBasket.ts that the cart already uses.
        if (
          couponCode &&
          (inAppMessage instanceof brazeSDK.ModalMessage || inAppMessage instanceof brazeSDK.FullScreenMessage) &&
          inAppMessage.buttons?.length
        ) {
          inAppMessage.buttons[0].subscribeToClickedEvent(async () => {
            const existingCoupon = useBasketsStore().basket?.couponItems?.find((item) => item.code === couponCode)
            const alreadyApplied =
              existingCoupon?.valid === true && existingCoupon?.statusCode !== NO_APPLICABLE_PROMOTION
            if (!existingCoupon && couponApplyInFlight.has(couponCode)) {
              dismissal.suppress()
              inAppMessage.closeMessage()
              return
            }
            const { applyCoupon } = useBasket()
            let success: boolean | undefined
            if (existingCoupon) {
              success = false
            } else {
              couponApplyInFlight.add(couponCode)
              try {
                success = await applyCoupon(couponCode)
              } finally {
                couponApplyInFlight.delete(couponCode)
              }
              const appliedItem = useBasketsStore().basket?.couponItems?.find((item) => item.code === couponCode)
              if (success && appliedItem?.statusCode === NO_APPLICABLE_PROMOTION) {
                success = false
              }
            }
            if (success === undefined) {
              toast.default(t('checkout.order_summary.iam_coupon_no_basket', { code: couponCode }), {
                duration: 5000,
                position: 'top',
              })
            } else if (alreadyApplied) {
              toast.error(t('checkout.order_summary.iam_coupon_already_applied'), {
                duration: 5000,
                position: 'top',
              })
            } else if (!success) {
              toast.error(t('checkout.order_summary.iam_coupon_apply_error', { code: couponCode }), {
                duration: 5000,
                position: 'top',
              })
            }
            $braze.debugLogger(`IAM coupon application: ${couponCode} — ${success ? 'success' : 'failed'}`)
            dismissal.suppress()
            inAppMessage.closeMessage()
          })
        }

        showMessage()
      }

      // Subscribe to in-app messages rather than using automaticallyShowInAppMessages().
      // These two methods MUST NOT coexist.
      //
      // The `rules` extras key gates display. The SDK has already spent the trigger by the time the
      // message reaches us, so campaigns gated by rules need a specific Braze setup to recover —
      // see docs/braze-campaign-rules.md. That setup makes "a suppressed message logs no impression"
      // load-bearing: its exclusion group keys off the impression, so never log one for a message we
      // did not show. Control messages are gated too, otherwise the holdout would log impressions
      // the variant never got.
      $braze.sdk.subscribeToInAppMessage((inAppMessage) => {
        const logSuppressed = (verdict: CampaignRuleVerdict) => $braze.debugLogger(`IAM suppressed: ${verdict.reason}`)
        const verdict = campaignRules.evaluate(inAppMessage.extras?.['rules'])

        // Only rules that read cart/wishlist state defer; everything else decides synchronously so
        // message handling keeps its original timing.
        if (verdict instanceof Promise) {
          verdict
            .then((resolved) => (resolved.show ? handleInAppMessage(inAppMessage) : logSuppressed(resolved)))
            // Nothing awaits this inside the SDK callback, so a rejection would be silent otherwise.
            .catch((error) => Sentry.captureException(error, { tags: { feature: 'braze-iam-rules' } }))
          return
        }

        return verdict.show ? handleInAppMessage(inAppMessage) : logSuppressed(verdict)
      })

      $braze.sdk.subscribeToContentCardsUpdates((cards) => {
        useContentCardStore().setOfferCards()
        $braze.debugLogger(`Content cards updated: ${cards.lastUpdated}`)
      })

      $braze.sdk.openSession()
      useBrazeTracking().syncLoggedInStatusIfAnonymous()
      useContentCardStore().init()
    },
    'page:finish'() {
      if (!useFeatureFlags().brazePageViewEnabled()) return

      useBrazeTracking().logCustomEvent('page_view', { url: window.location.href })
    },
  },
})
